// =====================================================================
// BFF realtime protocol — the browser side of the BFF WebSocket (`/bff/ws`).
//
// The BFF is the single browser-facing edge (apps/bff/src/app.ts + ws/hub.ts).
// Frames are JSON with a `kind` discriminator. Today the SPA drives a
// client-side liveness/RTT probe: it sends `ping` frames and the BFF answers
// `pong`, echoing the client's `n`/`ts` plus a server clock stamp. The pure
// helpers below (build/parse) are framework-free so the message logic is unit
// tested without a real socket; useBffPing wires them to a WebSocket + React.
// =====================================================================

// Outbound: a liveness/RTT probe. `n` is a monotonic counter, `ts` the client
// send clock (Date.now()); the BFF echoes both so we can match + time replies.
export interface PingFrame {
  kind: 'ping';
  n: number;
  ts: number;
}

// Inbound frames we react to. `hello` lands on connect; `pong` answers a ping.
// (Other server frames — dialer_outcome/error — are ignored by the probe.)
export interface PongFrame {
  kind: 'pong';
  n?: number;
  ts?: number;
  serverTs?: number;
}

// Builds the ping frame the BFF expects (matches the ClientFrame zod union in
// apps/bff/src/app.ts).
export function buildPing(n: number, now: number = Date.now()): PingFrame {
  return { kind: 'ping', n, ts: now };
}

// Parses a raw WS message into a PongFrame, or null if it isn't one. Tolerant:
// anything unparseable or of another `kind` is simply ignored (returns null).
export function parsePong(raw: unknown): PongFrame | null {
  if (typeof raw !== 'string') return null;
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof json !== 'object' || json === null) return null;
  const frame = json as Record<string, unknown>;
  if (frame.kind !== 'pong') return null;
  const pong: PongFrame = { kind: 'pong' };
  if (typeof frame.n === 'number') pong.n = frame.n;
  if (typeof frame.ts === 'number') pong.ts = frame.ts;
  if (typeof frame.serverTs === 'number') pong.serverTs = frame.serverTs;
  return pong;
}

// Round-trip time for a pong, in ms, derived from the echoed client `ts`.
// Returns null when the pong didn't echo a usable timestamp.
export function rttMs(pong: PongFrame, now: number = Date.now()): number | null {
  if (typeof pong.ts !== 'number') return null;
  return Math.max(0, now - pong.ts);
}

// Inbound `hello` — the BFF's first frame on every connection. `config` carries
// per-connection, server-decided flags; `singleWs` = skip the health-ping socket
// and read link health off the cockpit socket (plans/single-websocket/plan.md).
export interface HelloFrame {
  kind: 'hello';
  // Only an explicit `config.singleWs === true` enables — an absent/malformed
  // config (an older BFF, or anything unexpected) reads as OFF, so every
  // BFF↔bundle version pairing degrades to today's two-socket behaviour.
  singleWs: boolean;
  // `emailCheck` = the in-call email check can answer. Read the same way, so an
  // older BFF leaves the control disabled.
  emailCheck: boolean;
}

// Parses a raw WS message into a HelloFrame, or null if it isn't one. Tolerant,
// like parsePong: anything unparseable or of another `kind` returns null.
export function parseHello(raw: unknown): HelloFrame | null {
  if (typeof raw !== 'string') return null;
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof json !== 'object' || json === null) return null;
  const frame = json as Record<string, unknown>;
  if (frame.kind !== 'hello') return null;
  const config =
    typeof frame.config === 'object' && frame.config !== null
      ? (frame.config as Record<string, unknown>)
      : {};
  return {
    kind: 'hello',
    singleWs: config.singleWs === true,
    emailCheck: config.emailCheck === true,
  };
}

// =====================================================================
// BFF link health — metrics derived over the ping/pong stream for the Twilio
// control box's connection-quality readout (RTT, jitter, dropped pings). These
// are pure + framework-free so they're unit-tested without a socket; the
// stateful sampling (windows, miss sweep, online events) lives in useBffPing.
// =====================================================================

// Population standard deviation of a sample — we use it as WS "jitter": how much
// the RTT bounces around. 0 for fewer than 2 samples (nothing to vary yet).
export function stddev(xs: number[]): number {
  if (xs.length < 2) return 0;
  const mean = xs.reduce((a, b) => a + b, 0) / xs.length;
  const variance = xs.reduce((a, b) => a + (b - mean) ** 2, 0) / xs.length;
  return Math.sqrt(variance);
}

// Coarse connection-quality bucket for the at-a-glance dot. Inputs are the live
// link metrics; `stale` means pongs have stopped arriving (the caller derives it
// from the age of the last pong) — a packet-reliability proxy for a WS that has
// no per-packet ACKs of its own.
export type ConnQuality = 'excellent' | 'good' | 'degraded' | 'offline';

export function deriveQuality(m: {
  open: boolean;
  online: boolean;
  rttMs: number | null;
  jitterMs: number;
  stale: boolean;
}): ConnQuality {
  if (!m.open || !m.online) return 'offline';
  if (m.stale) return 'degraded'; // pongs went quiet — packets dropping/blocked
  if (m.rttMs === null) return 'good'; // open, but no timed pong yet
  if (m.rttMs < 150 && m.jitterMs < 50) return 'excellent';
  if (m.rttMs < 400) return 'good';
  return 'degraded';
}

// =====================================================================
// Dialer outcomes — the BFF fans these out as {v:1, kind:"dialer_outcome",
// event}. A client subscribes to a pool with `subscribe_pool`, then renders the
// events it receives. Ids arrive as strings (the BFF coerces the core's UInt64s
// — see apps/bff/src/events/schema.ts), so this side just displays them.
// =====================================================================

// Outbound: ask the BFF to route a pool's outcomes to this socket.
export interface SubscribeFrame {
  kind: 'subscribe_pool';
  poolId: string;
}

export function buildSubscribe(poolId: string): SubscribeFrame {
  return { kind: 'subscribe_pool', poolId };
}

// A dialer outcome (flat; mirrors apps/bff/src/events/schema.ts). Fields are
// optional so an unexpected shape never crashes the panel.
export interface DialerOutcome {
  type: string;
  at?: string;
  client_id?: string | null;
  pool_id?: string | null;
  sdr_id?: string | null;
  call_id?: string | null;
  contact_id?: string | null;
  state?: string | null;
  end_reason?: string | null;
  duration_ms?: number | null;
  dial_type?: string | null;
}

// A parsed dialer_outcome frame: the event plus the BFF's receive stamp, used to
// measure pipe latency (event.at = core, bffReceivedAt = BFF, browser = on arrival).
export interface ParsedOutcome {
  outcome: DialerOutcome;
  // Epoch ms the BFF stamped when it consumed the outcome from SQS. Absent on
  // frames from an older BFF.
  bffReceivedAt?: number;
}

// Parses a raw WS message into the dialer_outcome it carries (event +
// bffReceivedAt), or null if it's another kind / malformed (tolerant, like parsePong).
export function parseOutcome(raw: unknown): ParsedOutcome | null {
  if (typeof raw !== 'string') return null;
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof json !== 'object' || json === null) return null;
  const frame = json as Record<string, unknown>;
  if (frame.kind !== 'dialer_outcome') return null;
  const event = frame.event;
  if (typeof event !== 'object' || event === null) return null;
  if (typeof (event as Record<string, unknown>).type !== 'string') return null;
  const result: ParsedOutcome = { outcome: event as unknown as DialerOutcome };
  if (typeof frame.bffReceivedAt === 'number') result.bffReceivedAt = frame.bffReceivedAt;
  return result;
}
